import type { ActionFields } from '@tribeplatform/gql-client/graphql'
import type { ClientError } from '@tribeplatform/gql-client/lib'
import {
  Action,
  MutationBlockMemberArgs,
  SpaceType,
} from '@tribeplatform/gql-client/types'

import {
  useMutation,
  UseMutationOptions,
} from '../../lib/react-query/useMutation.js'
import { useQueryClient } from '../../lib/react-query/useQueryClient.js'
import { useTribeClient } from '../../useTribeClient.js'
import { getAuthMemberKey } from '../../utils/keys/authToken.keys.js'
import { getCheckMembersContactabilityKey } from '../../utils/keys/messaging.key.js'
import { getSpaceKey, getSpacesKey } from '../../utils/keys/space.key.js'

export const useBlockMember = (options?: {
  fields?: ActionFields
  useMutationOptions?: UseMutationOptions<
    Action,
    ClientError,
    MutationBlockMemberArgs
  >
}) => {
  const { useMutationOptions } = options || {}
  const { client } = useTribeClient()
  const queryClient = useQueryClient()

  return useMutation<Action, ClientError, MutationBlockMemberArgs>(
    (input: MutationBlockMemberArgs) => client.members.blockMember(input),
    {
      onSettled: () => {
        const memberKey = getAuthMemberKey()
        const membersContactabilityKey = getCheckMembersContactabilityKey()
        queryClient.invalidateQueries(memberKey)
        queryClient.invalidateQueries(membersContactabilityKey)

        // now all spaces where member is a participant need to be refetched
        // to get updated `canSendPrivateMessage` permission
        const pmSpacesKey = getSpacesKey({
          variables: { type: [SpaceType.PrivateMessage] },
        })
        queryClient.removeQueries(pmSpacesKey)

        const spaceKey = getSpaceKey()
        queryClient.removeQueries(spaceKey)
      },
      ...useMutationOptions,
    },
  )
}
